import { useState } from "react"
import PropTypes from "prop-types"
import { api } from "utils/api"
import { pluralize } from "utils/helpers"
import { Table, BlankState } from "components/ui"
import { Button } from "@planningcenter/tapestry-react"
import NewAssignmentType from "./NewAssignmentType"
import { colors } from "components/shared/admin_colors"
import { css } from "@emotion/core"

const renderCapacity = (row, _column) => {
  return `${row.totalCapacity - row.availableCapacity} / ${row.totalCapacity}`
}

const AssignmentTypes = ({ assignmentTypes, assignmentTypesURL }) => {
  const [assignments, setAssignments] = useState(
    assignmentTypes.map(x => ({
      ...x,
      capacity: x.totalCapacity - x.availableCapacity
    }))
  )
  const [saveStatus, setSaveStatus] = useState(null)

  const renderDeleteButton = (row, _column) => {
    return (
      row.canDelete && (
        <Button
          title="Delete"
          size="xs"
          variant="outline"
          theme="destroy"
          onClick={e => handleDelete(row, e)}
        />
      )
    )
  }

  const columns = [
    {
      cell: "name",
      label: "Name",
      style: { width: "40%" }
    },
    {
      cell: "totalAreas",
      label: "Areas",
      style: { width: "25%" }
    },
    {
      cell: "capacity",
      label: "Capacity",
      render: renderCapacity,
      style: { width: "25%" }
    },
    {
      cell: "actions",
      label: "Assignment type actions",
      hideHeaderLabel: true,
      style: { textAlign: "right", width: "10%" },
      render: renderDeleteButton
    }
  ]

  const handleDragEnd = newItems => {
    return api
      .put(`${assignmentTypesURL}/order`, {
        assignment_type_ids: newItems.map(x => x.id)
      })
      .then(resp => {
        setAssignments(resp.data)
        return resp.data
      })
      .catch(resp => {
        // Force item reset if failed.
        setAssignments(assignments.concat([]))
        return Promise.reject(resp)
      })
  }

  const handleRowClick = row => window.location.assign(`${row.paths.show}`)

  const styles = {
    newRecordCell: css`
      background-color: ${colors.neutral_080};
    `
  }

  const handleNewButtonClick = () =>
    setAssignments([
      {
        id: "new",
        isClickDisabled: true,
        // eslint-disable-next-line react/display-name
        render: (_row, _columns) => (
          <>
            <td css={styles.newRecordCell} />
            <td css={styles.newRecordCell}>
              <NewAssignmentType onCancel={handleCancel} onSave={handleSave} />
            </td>
            <td css={styles.newRecordCell} colSpan="3" />
          </>
        )
      },
      ...assignments
    ])

  const handleCancel = () => setAssignments(assignments.filter(x => x.id !== "new"))

  const handleSave = params => {
    api
      .post(assignmentTypesURL, {
        assignment_type: { ...params, position: assignments.length + 1 }
      })
      .then(resp => {
        setAssignments(assignments.concat(resp.data).filter(x => x.id !== "new"))
        return handleRowClick(resp.data)
      })
  }

  const handleDelete = (row, e) => {
    setSaveStatus({
      text: "Saving...",
      icon: "spinner"
    })

    e.stopPropagation()

    return api
      .del(row.paths.show)
      .then(resp => {
        setSaveStatus({
          text: "Deleted!",
          icon: "general#check",
          type: "success"
        })

        setAssignments(resp.data)
      })
      .catch(_error => {
        setSaveStatus({
          text: "Deletion failed.",
          icon: "general#exclamation-triangle",
          type: "error"
        })
      })
      .finally(_r => setTimeout(() => setSaveStatus(null), 2000))
  }

  const hasAssignmentType = assignments.length > 0

  return (
    <>
      {hasAssignmentType ? (
        <Table
          heading={{
            headerText: pluralize("assignment type", "assignment types", assignments.length),
            style: { alignItems: "center" }
          }}
          headingChildren={
            <Button title="New type" theme="create" onClick={handleNewButtonClick} />
          }
          columns={columns}
          rows={assignments}
          draggableType="handle"
          onDragEnd={handleDragEnd}
          onRowClick={handleRowClick}
          status={saveStatus}
        />
      ) : (
        <>
          <BlankState
            imageURL="/static/blank-states/registrations-assignment-types.svg"
            placeholderText={`Assignments help you sort your participants into cabins, buses, teams, or anything else! Start by creating your assignment type, such as "Classes," and then within that type, you'll add your areas, such as "Preschool" and "K-2."`}
          />
          <div className="blank-state__msg blank-state__msg--wide">
            <NewAssignmentType
              onSave={handleSave}
              saveButtonSize="md"
              saveLabel="Add assignment type"
            />
          </div>
        </>
      )}
    </>
  )
}

AssignmentTypes.propTypes = {
  assignmentTypes: PropTypes.arrayOf(PropTypes.object),
  assignmentTypesURL: PropTypes.string
}

export default AssignmentTypes
